import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { resendConfirmation } from '../../services/notificationService';
import { useNotification } from '../../notification/NotificationContext';
import { getStoredUser } from '../../utils/auth';
import './EmailVerificationBanner.css';

const DISMISS_KEY = 'emailVerifyBannerDismissed';

const EmailVerificationBanner = () => {
  const { showNotification } = useNotification();
  const [user, setUser] = useState(getStoredUser());
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const refreshUser = useCallback(() => {
    setUser(getStoredUser());
  }, []);

  useEffect(() => {
    window.addEventListener('storage', refreshUser);
    window.addEventListener('auth:user-updated', refreshUser);
    return () => {
      window.removeEventListener('storage', refreshUser);
      window.removeEventListener('auth:user-updated', refreshUser);
    };
  }, [refreshUser]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!user || user.emailConfirmed !== false) return null;
  if (dismissed) return null;

  const handleResend = async () => {
    if (!user.email || sending || cooldown > 0) return;
    setSending(true);
    try {
      await resendConfirmation(user.email);
      showNotification('Confirmation email resent!', 'success');
      setCooldown(60);
    } catch (err) {
      showNotification(err.message || 'Failed to resend confirmation email.', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="evb-bar" role="status">
      <div className="evb-content">
        <AlertTriangle size={16} className="evb-ic" />
        <span className="evb-text">
          Please verify your email address.{' '}
          <button
            type="button"
            className="evb-link"
            onClick={handleResend}
            disabled={sending || cooldown > 0}
          >
            {sending
              ? 'Sending...'
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : 'Resend confirmation email'}
          </button>
        </span>
      </div>
      <button
        type="button"
        className="evb-close"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
